// Mail-consent (NGOP-1061) gating and assembly, kept free of React/redux so it can be unit-tested
// without the full component graph (mirrors the backend's SendConsentMailIfAny extraction).

// FrontendSettings values arrive as strings ("true"/"false"), hence the === "true" comparisons.
export type MailConsentSettings = Record<string, string | undefined>;

export interface ConsentOption {
    // Doubles as the gui-translation key and the consent id sent to the backend, e.g. "MAIL_CONSENT_1".
    key: string;
    required: boolean;
}

// The configurable, numbered consents (1..3); the appendix is handled separately (no checkbox).
const CONSENT_NUMBERS = [1, 2, 3];

const isConsentEnabled = (settings: MailConsentSettings, n: number) => settings[`mail_consent_${n}_enabled`] === "true";

// Enabled, numbered consents the user can tick, in display order.
export const buildConsentOptions = (settings: MailConsentSettings): ConsentOption[] =>
    CONSENT_NUMBERS.filter(n => isConsentEnabled(settings, n)).map(n => ({
        key: `MAIL_CONSENT_${n}`,
        required: settings[`mail_consent_${n}_required`] === "true",
    }));

export const isAppendixEnabled = (settings: MailConsentSettings) => settings.mail_consent_appendix_enabled === "true";

// Required options the user has not ticked. Empty => send may proceed.
export const unmetRequiredConsents = (options: ConsentOption[], checked: Record<string, boolean>): string[] =>
    options.filter(o => o.required && !checked[o.key]).map(o => o.key);

// Consent ids to record: every ticked option (in display order), plus the appendix when enabled.
// MAIL_CONSENT_APPENDIX is informational text with no checkbox, so it rides along only when a real
// consent was ticked — an appendix-only situation records no approval and sends no consent mail.
export const collectSelectedConsents = (
    options: ConsentOption[],
    checked: Record<string, boolean>,
    appendixEnabled: boolean
): string[] => {
    const selected = options.filter(o => checked[o.key]).map(o => o.key);
    if (appendixEnabled && selected.length > 0) {
        selected.push("MAIL_CONSENT_APPENDIX");
    }
    return selected;
};
